// src/components/User/UserModal.jsx
// Modal component for adding/editing users

import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axiosConfig';

/**
 * UserModal Component
 * A modal dialog for creating or editing users
 * 
 * @param {boolean} isOpen - Controls whether modal is visible
 * @param {Function} onClose - Callback when modal should close
 * @param {Function} onSave - Callback when form is submitted
 * @param {Object} user - Existing user data (for edit mode)
 * @param {boolean} isSaving - Whether save operation is in progress
 */
const UserModal = ({ isOpen, onClose, onSave, user, isSaving }) => {
  
  // Form state - stores all input values
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    employeeId: '',
    isActive: true
  });

  // Employee dropdown options
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  /**
   * Fetch employees from backend for dropdown
   */
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        const response = await axiosInstance.get('/employees');
        const employeeData = Array.isArray(response.data) 
          ? response.data 
          : response.data.data || [];
        setEmployees(employeeData);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setEmployees([]);
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  /**
   * When user prop changes (for editing), update the form
   * This runs when component mounts or when 'user' changes
   */
  useEffect(() => {
    if (user) {
      // Edit mode - populate form with existing data
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't populate password for security
        role: user.role || '',
        employeeId: user.employeeId || '',
        isActive: user.isActive ?? true
      });
    } else {
      // Add mode - clear the form
      setFormData({
        username: '',
        email: '',
        password: '',
        role: '',
        employeeId: '',
        isActive: true
      });
    }
  }, [user]);

  /**
   * Handle input changes
   * Updates formData state when user types in any field
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim() || !formData.email.trim() || !formData.role) {
      alert('Please fill out all required fields (Username, Email, and Role)');
      return;
    }

    // For add mode, password is required
    if (!user && !formData.password.trim()) {
      alert('Password is required when creating a new user');
      return;
    }

    await onSave(formData); // assume save succeeds

    // Clear password field after save (add mode)
    if (!user) {
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (isSaving) return;

    setFormData(prev => ({
      ...prev,
      password: ''
    }));

    onClose();
  };

  // Don't render anything if modal is not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="user-modal-overlay">
      <div className="user-modal-content">
        
        {/* Modal Header */}
        <header className="user-modal-header">
          <h3 className="user-modal-title">
            {user ? `Edit User: ${user.username}` : 'Add New User'}
          </h3>
          <button 
            className="user-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </header>

        {/* Modal Body - The Form */}
        <div className="user-modal-body">
          <form id="userForm" onSubmit={handleSubmit}>
            <div className="user-form-grid">
              
              {/* Username Field */}
              <label htmlFor="username">
                Username <span style={{color: 'red'}}>*</span>
              </label>
              <input 
                type="text"
                id="username"
                name="username"
                className="user-custom-input"
                placeholder="e.g., sanduni.fernando"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={isSaving}
              />

              {/* Email Field */}
              <label htmlFor="email">
                Email <span style={{color: 'red'}}>*</span>
              </label>
              <input 
                type="email"
                id="email"
                name="email"
                className="user-custom-input"
                placeholder="e.g., sanduni.fernando@company.lk"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSaving}
              />

              {/* Password Field */}
              <label htmlFor="password">
                Password {!user && <span style={{color: 'red'}}>*</span>}
              </label>
              <input 
                type="password"
                id="password"
                name="password"
                className="user-custom-input"
                placeholder={user ? "Leave blank to keep current password" : "e.g., User@123"}
                value={formData.password}
                onChange={handleChange}
                required={!user}
                disabled={isSaving}
              />

              {/* Role Field */}
              <label htmlFor="role">
                Role <span style={{color: 'red'}}>*</span>
              </label>
              <select 
                id="role"
                name="role"
                className="user-custom-select"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={isSaving}
              >
                <option value="">Select Role</option>
                <option value="SUPERADMIN">SUPERADMIN</option>
                <option value="ADMIN">ADMIN</option>
                <option value="USER">USER</option>
              </select>

              {/* Employee ID Dropdown */}
              <label htmlFor="employeeId">Employee</label>
              <select 
                id="employeeId"
                name="employeeId"
                className="user-custom-select"
                value={formData.employeeId}
                onChange={handleChange}
                disabled={isSaving || isLoadingEmployees}
              >
                <option value="">
                  {isLoadingEmployees ? 'Loading employees...' : 'Select Employee (Optional)'}
                </option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName} ({employee.employeeCode})
                  </option>
                ))}
              </select>

              {/* Active Checkbox */}
              <label htmlFor="isActive">Active</label>
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleCheckboxChange}
                disabled={isSaving}
              />
            </div>

            {/* Modal Footer - Action Buttons */}
            <footer className="user-modal-footer">
              <button
                className="user-btn user-btn-cancel"
                onClick={handleClose}
                disabled={isSaving}
                type="button"
              >
                Cancel
              </button>

              <button
                className="user-btn user-btn-save"
                disabled={isSaving}
                type="submit"
              >
                {isSaving ? 'Saving...' : 'Save User'}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserModal;